<template>
    <section class="acc" dir="rtl" aria-live="polite">

        <!-- ===== טעינה ===== -->
        <div v-if="loading && !loaded" class="acc-card">
            <v-skeleton-loader type="heading, text" class="pa-6" />
            <v-skeleton-loader type="image" height="14" class="px-6 pb-6" />
        </div>

        <!-- ===== שגיאה ===== -->
        <div v-else-if="error && !loaded" class="acc-card acc-error">
            <p>לא הצלחנו לטעון את פרטי החבילה. בדקו את החיבור ונסו שוב.</p>
            <v-btn color="#0d2c6d" dark depressed @click="getPlanDetails">נסו שוב</v-btn>
        </div>

        <div v-else-if="loaded" class="acc-card">

            <!-- ===== החבילה ===== -->
            <header class="acc-plan" :class="'is-' + planKind">
                <div class="acc-plan__art">
                    <img src="@/assets/settings/plan-4.svg" alt="">
                </div>
                <div class="acc-plan__text">
                    <span class="acc-kind" :class="'is-' + planKind">
                        <v-icon small>{{ kindIcon }}</v-icon>
                        {{ kindLabel }}
                    </span>
                    <h2 class="acc-plan__name">{{ planDetails.name }}</h2>
                    <p class="acc-plan__status">{{ statusLine }}</p>
                </div>
                <v-btn class="acc-upgrade" depressed large @click="upgradePlan">
                    <v-icon left>mdi-arrow-up-bold-circle-outline</v-icon>
                    {{ isPaid ? 'החלפת חבילה' : 'שדרוג חבילה' }}
                </v-btn>
            </header>

            <div class="acc-body">
                <!-- ===== אחסון: מד עגול ===== -->
                <section class="acc-storage" aria-labelledby="acc-storage">
                    <h3 id="acc-storage" class="acc-h">אחסון</h3>
                    <div class="acc-ring" :class="'is-' + storageLevel" role="progressbar" :aria-valuenow="Math.round(storagePercent)"
                        aria-valuemin="0" aria-valuemax="100" :aria-label="'אחסון בשימוש: ' + Math.round(storagePercent) + '%'">
                        <svg viewBox="0 0 120 120" aria-hidden="true">
                            <circle class="acc-ring__track" cx="60" cy="60" r="52" />
                            <circle class="acc-ring__fill" cx="60" cy="60" r="52" :stroke-dasharray="ringLength"
                                :stroke-dashoffset="ringOffset" />
                        </svg>
                        <div class="acc-ring__center">
                            <span class="acc-ring__pct acc-num">{{ Math.round(storagePercent) }}%</span>
                            <span class="acc-ring__label">בשימוש</span>
                        </div>
                    </div>
                    <p class="acc-storage__used">
                        <b class="acc-num">{{ formatSize(usedMB) }}</b> מתוך <span class="acc-num">{{ formatSize(limitMB) }}</span>
                    </p>
                    <p class="acc-muted acc-storage__left">נשארו <b class="acc-num">{{ formatSize(remainingMB) }}</b></p>
                </section>

                <!-- ===== מה כלול ===== -->
                <section class="acc-features" aria-labelledby="acc-included">
                    <h3 id="acc-included" class="acc-h">מה כלול בחבילה</h3>
                    <ul class="acc-list">
                        <li>
                            <span class="acc-tile"><img src="@/assets/settings/plan-9.svg" alt=""></span>
                            <span class="acc-list__label">שיחות במקביל</span>
                            <b class="acc-list__value acc-num">{{ planDetails.channels }}</b>
                        </li>
                        <li>
                            <span class="acc-tile"><img src="@/assets/settings/plan-7.svg" alt=""></span>
                            <span class="acc-list__label">אנשי קשר</span>
                            <b class="acc-list__value acc-num">{{ formatCount(planDetails.contacts) }}</b>
                        </li>
                        <li>
                            <span class="acc-tile"><img src="@/assets/settings/plan-2.svg" alt=""></span>
                            <span class="acc-list__label">אחסון</span>
                            <b class="acc-list__value acc-num">{{ planDetails.storage }}GB</b>
                        </li>
                    </ul>
                </section>
            </div>

            <!-- אחסון כמעט מלא / מלא -->
            <div v-if="storageLevel !== 'ok'" class="acc-notice acc-notice--wide">
                <v-icon small color="#c2410c">mdi-alert-circle-outline</v-icon>
                <span>{{ storageLevel === 'full' ? 'האחסון מלא, ואי אפשר להקליט הודעות חדשות.' : 'האחסון כמעט מלא.' }}
                    אפשר למחוק הודעות ישנות, או לעבור לחבילה עם יותר אחסון.</span>
                <v-btn text small color="#0d2c6d" class="acc-notice__btn" @click="upgradePlan">לחבילות</v-btn>
            </div>

            <!-- ===== חיוב - רק בחבילה בתשלום ===== -->
            <section v-if="isPaid" class="acc-billing" aria-label="חיוב">
                <div class="acc-billing__item">
                    <span class="acc-tile"><img src="@/assets/settings/plan-8.svg" alt=""></span>
                    <div>
                        <span class="acc-billing__label">החיוב הבא</span>
                        <b class="acc-billing__value acc-num">{{ nextBillingDate || '—' }}</b>
                    </div>
                </div>
                <div class="acc-billing__item">
                    <span class="acc-tile"><img src="@/assets/settings/plan-3.svg" alt=""></span>
                    <div>
                        <span class="acc-billing__label">סכום חודשי</span>
                        <b class="acc-billing__value acc-num">{{ formatMoney(planDetails.price) }}</b>
                    </div>
                </div>
                <div v-if="!planDetails.hasHok" class="acc-notice">
                    <v-icon small color="#c2410c">mdi-credit-card-off-outline</v-icon>
                    <span>לא מוגדרת הוראת קבע לחבילה. כדי שהחבילה תמשיך לפעול, צריך להגדיר אמצעי תשלום.</span>
                </div>
            </section>
        </div>

        <PlansDialog v-model="plansDialog" v-if="plansDialog" />

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </section>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import PlansDialog from '@/components/settingspage/dialogs/PlansDialog'

const dateFormat = new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'numeric', year: 'numeric' });
const countFormat = new Intl.NumberFormat('he-IL');

export default {
    components: {
        SnackBar,
        PlansDialog
    },
    data: () => ({
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        loading: false,
        loaded: false,
        error: false,
        plansDialog: false,
        planDetails: {},
        ringReady: false,   // המד העגול מתמלא פעם אחת, אחרי הטעינה
    }),
    computed: {
        billType() {
            return this.planDetails.bill_type;
        },
        isTrial() {
            return this.billType === 'trial';
        },
        isPaid() {
            return ['postpaid', 'prepaid'].includes(this.billType) && Number(this.planDetails.price) > 0;
        },
        // תאריך סיום הניסיון - רק בחבילת ניסיון. בכל חבילה אחרת אין לו משמעות, גם אם נשאר שמור
        trialEnds() {
            if (!this.isTrial || !this.planDetails.tryingFinishDate) return '';
            const date = new Date(this.planDetails.tryingFinishDate);
            return isNaN(date.getTime()) ? '' : dateFormat.format(date);
        },
        // סוג החבילה - לתגית ולצבע הפס
        planKind() {
            if (this.isPaid) return 'paid';
            if (this.isTrial) return 'trial';
            return 'free';
        },
        kindLabel() {
            return { paid: 'חבילה בתשלום', trial: 'תקופת ניסיון', free: 'חבילה חינמית' }[this.planKind];
        },
        kindIcon() {
            return { paid: 'mdi-crown', trial: 'mdi-timer-sand', free: 'mdi-gift-outline' }[this.planKind];
        },
        // מתחת לשם: רק המידע הנוסף (הסוג כבר בתגית)
        statusLine() {
            if (this.isPaid) return `${this.formatMoney(this.planDetails.price)} לחודש`;
            if (this.isTrial) return this.trialEnds ? `עד ${this.trialEnds}` : 'ללא תאריך סיום';
            return 'ללא תשלום חודשי';
        },
        usedMB() {
            return Math.max(0, Number(this.planDetails.totalStorageUsed) || 0);
        },
        limitMB() {
            return (Number(this.planDetails.storage) || 0) * 1000; // GB -> MB, כמו בשרת
        },
        remainingMB() {
            return Math.max(0, this.limitMB - this.usedMB);
        },
        storagePercent() {
            return this.limitMB ? Math.min(100, (this.usedMB / this.limitMB) * 100) : 0;
        },
        ringLength() {
            return 2 * Math.PI * 52;
        },
        ringOffset() {
            const pct = this.ringReady ? this.storagePercent : 0;
            return this.ringLength * (1 - pct / 100);
        },
        storageLevel() {
            if (this.storagePercent >= 100) return 'full';
            if (this.storagePercent >= 85) return 'warn';
            return 'ok';
        },
        // החיוב הבא: אותו יום בחודש כמו תחילת התשלום, בפעם הבאה מהיום והלאה.
        // בחודש קצר יותר (למשל 31 בפברואר) - היום האחרון של החודש
        nextBillingDate() {
            if (!this.planDetails.startPayDate) return '';
            const start = new Date(this.planDetails.startPayDate);
            if (isNaN(start.getTime())) return '';
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const onDay = (year, month) => new Date(year, month, Math.min(start.getDate(), new Date(year, month + 1, 0).getDate()));
            let next = onDay(today.getFullYear(), today.getMonth());
            if (next < today) next = onDay(today.getFullYear(), today.getMonth() + 1);
            if (next < start) next = start;
            return dateFormat.format(next);
        },
    },
    methods: {
        async getPlanDetails() {
            this.loading = true;
            this.error = false;
            try {
                const token = localStorage.getItem("token");
                const res = await fetch(process.env.VUE_APP_BASE_URL + `/settings/get_plan_details`, ApiServices.requestOptions("GET", "", token));
                if (res.status !== 200) throw new Error(`status ${res.status}`);
                this.planDetails = await res.json();
                this.loaded = true;
                // רגע אחרי הציור - כדי שהמעבר של המד העגול יתחיל מ-0
                setTimeout(() => { this.ringReady = true; }, 60);
            } catch (error) {
                console.error(error);
                this.error = true;
                this.showSnackBar("לא הצלחנו לטעון את פרטי החבילה", "red");
            } finally {
                this.loading = false;
            }
        },
        // MB -> טקסט: מ-1000MB ומעלה ב-GB (כמו החישוב בשרת: 1GB = 1000MB)
        formatSize(mb) {
            if (mb >= 1000) {
                const gb = mb / 1000;
                return `${Number.isInteger(gb) ? gb : gb.toFixed(1)}GB`;
            }
            return `${Math.round(mb)}MB`;
        },
        formatCount(value) {
            return countFormat.format(Number(value) || 0);
        },
        formatMoney(value) {
            const n = Math.round((Number(value) || 0) * 100) / 100;
            return `${Number.isInteger(n) ? n : n.toFixed(2)} ₪`;
        },
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },
        upgradePlan() {
            this.plansDialog = true;
        },
    },
    mounted() {
        this.getPlanDetails();
    },
}
</script>

<style scoped>
.acc {
    --ink: #0d2c6d;
    --ink-soft: #3d5488;
    --gold: #ffb91a;
    --paper: #f5f7fb;
    --line: #e3e8f2;
    --muted: #6b7690;
    --warn: #c2410c;
    font-family: 'Heebo', sans-serif;
    color: var(--ink);
    width: 100%;
    max-width: 920px;
    box-sizing: border-box;
    margin: 24px auto;
    padding: 0 16px;
}

.acc-card {
    background: #fff;
    border: 1px solid var(--line);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 10px 30px -18px rgba(13, 44, 109, .35);
}

.acc-muted { color: var(--muted); font-size: 14px; }
.acc-num { font-variant-numeric: tabular-nums; }

/* ===== פס החבילה ===== */
.acc-plan {
    position: relative;
    background: var(--ink);
    color: #fff;
    padding: 26px 30px;
    display: flex;
    align-items: center;
    gap: 22px;
    overflow: hidden;
}

/* הד של המד העגול בפינת הפס - אותה צורה, בקנה מידה גדול */
.acc-plan::after {
    content: "";
    position: absolute;
    inset-inline-end: -70px;
    top: -90px;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    border: 26px solid rgba(255, 185, 26, .14);
    pointer-events: none;
}

.acc-plan__art {
    width: 96px;
    height: 96px;
    flex-shrink: 0;
    border-radius: 22px;
    background: #fff;
    display: grid;
    place-items: center;
    box-shadow: 0 8px 20px -10px rgba(0, 0, 0, .45);
}

.acc-plan__art img { width: 72px; height: 72px; object-fit: contain; }

.acc-plan__text { flex: 1; min-width: 0; position: relative; z-index: 1; }

/* חבילה בתשלום: פס זהב בתחתית הפס הכחול */
.acc-plan.is-paid { box-shadow: inset 0 -5px 0 var(--gold); }

/* התגית של סוג החבילה */
.acc-kind {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    padding: 4px 12px 4px 14px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
}

.acc-kind.is-paid { background: var(--gold); color: var(--ink); }
.acc-kind.is-paid .v-icon { color: var(--ink) !important; }
.acc-kind.is-free { background: rgba(255, 255, 255, .12); color: #fff; box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, .55); }
.acc-kind.is-free .v-icon { color: #fff !important; }
.acc-kind.is-trial { color: var(--gold); box-shadow: inset 0 0 0 1.5px var(--gold); }
.acc-kind.is-trial .v-icon { color: var(--gold) !important; }

.acc-plan__name {
    margin: 0;
    font-size: 36px;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.1;
}

.acc-plan__status { margin: 6px 0 0; font-size: 16px; color: rgba(255, 255, 255, .82); }

.acc .acc-upgrade.v-btn {
    position: relative;
    z-index: 1;
    background-color: var(--gold) !important;
    color: var(--ink) !important;
    font-weight: 700;
    letter-spacing: 0;
    border-radius: 12px;
    flex-shrink: 0;
}

.acc .acc-upgrade.v-btn:focus-visible { outline: 3px solid #fff; outline-offset: 2px; }

.acc-h { margin: 0 0 14px; font-size: 17px; font-weight: 700; }

/* ===== גוף: אחסון + מה כלול ===== */
.acc-body {
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
}

.acc-storage {
    padding: 26px 30px;
    text-align: center;
    background: var(--paper);
    border-inline-end: 1px solid var(--line);
}

.acc-storage .acc-h { text-align: start; }

.acc-ring {
    position: relative;
    width: 176px;
    height: 176px;
    margin: 4px auto 12px;
}

.acc-ring svg { width: 100%; height: 100%; transform: rotate(-90deg); }

.acc-ring__track { fill: none; stroke: #e2e7f1; stroke-width: 12; }

.acc-ring__fill {
    fill: none;
    stroke: var(--gold);
    stroke-width: 12;
    stroke-linecap: round;
    transition: stroke-dashoffset 1.1s cubic-bezier(.22, .9, .3, 1);
}

.acc-ring.is-warn .acc-ring__fill,
.acc-ring.is-full .acc-ring__fill { stroke: var(--warn); }

.acc-ring__center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.acc-ring__pct { font-size: 38px; font-weight: 800; line-height: 1; }
.acc-ring.is-warn .acc-ring__pct, .acc-ring.is-full .acc-ring__pct { color: var(--warn); }
.acc-ring__label { margin-top: 4px; font-size: 13px; color: var(--muted); }

.acc-storage__used { margin: 0; font-size: 18px; color: var(--ink-soft); }
.acc-storage__used b { color: var(--ink); font-weight: 800; }
.acc-storage__left { margin: 2px 0 0; }

.acc-features { padding: 26px 30px; }

.acc-list { list-style: none; padding: 0; margin: 0; }

.acc-list li {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 0;
    border-bottom: 1px solid var(--line);
}

.acc-list li:last-child { border-bottom: none; }

/* האיורים כבר כוללים ריבוע משלהם - בלי מסגרת נוספת, ובגודל שרואים את הציור */
.acc-tile {
    width: 52px;
    height: 52px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
}

.acc-tile img { width: 52px; height: 52px; object-fit: contain; }

.acc-list__label { flex: 1; font-size: 16px; color: var(--ink-soft); }
.acc-list__value { font-size: 26px; font-weight: 800; }

/* ===== חיוב ===== */
.acc-billing {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 40px;
    padding: 22px 30px;
    border-top: 1px solid var(--line);
}

.acc-billing__item { display: flex; align-items: center; gap: 14px; }
.acc-billing__label { display: block; font-size: 14px; color: var(--muted); }
.acc-billing__value { display: block; font-size: 22px; font-weight: 800; }

/* ===== הודעות ===== */
.acc-notice {
    flex-basis: 100%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 8px;
    padding: 10px 14px;
    border-radius: 12px;
    background: rgba(194, 65, 12, .07);
    color: var(--warn);
    font-size: 14px;
    font-weight: 500;
}

.acc-notice span { flex: 1; min-width: 200px; }
.acc-notice--wide { margin: 18px 30px 22px; width: auto; }
.acc .acc-notice__btn.v-btn { font-weight: 700; letter-spacing: 0; }

/* ===== שגיאה ===== */
.acc-error { padding: 40px 24px; text-align: center; color: var(--ink-soft); }
.acc-error p { margin-bottom: 16px; }

@media (max-width: 760px) {
    .acc-body { grid-template-columns: 1fr; }
    .acc-storage { border-inline-end: none; border-bottom: 1px solid var(--line); }
}

@media (max-width: 600px) {
    .acc { margin: 12px auto; padding: 0 8px; }
    .acc-plan { flex-wrap: wrap; padding: 22px 20px; gap: 16px; }
    .acc-plan__art { width: 72px; height: 72px; border-radius: 18px; }
    .acc-plan__art img { width: 52px; height: 52px; }
    .acc-plan__name { font-size: 28px; }
    .acc .acc-upgrade.v-btn { flex-basis: 100%; }
    .acc-storage, .acc-features { padding: 22px 20px; }
    .acc-billing { padding: 20px; }
    .acc-notice--wide { margin: 16px 20px 20px; }
}

@media (prefers-reduced-motion: reduce) {
    .acc-ring__fill { transition: none; }
}
</style>

<!-- הסגנונות הכלליים מהגרסה הקודמת, בלי שינוי: הבלוק לא scoped, ולכן הם חלים על כל האתר -
     ייתכן שרכיבים אחרים משתמשים בהם (box, box-style, gradient-yellow-button-background...). העיצוב החדש לא משתמש בהם -->
<style>
.gradient-yellow-button-background {
    background: linear-gradient(0deg, rgba(255, 185, 26, 1) 1%, rgba(255, 185, 26, 0.8127626050420168) 70%);
}


.div-content {
    display: flex;
    justify-content: center;
    align-items: center;
}

.span-content-large {
    color: #0d2c6d;
    font-weight: 500;
    font-size: large;
}

.span-content-large-bold {
    color: #0d2c6d;
    font-weight: bold;
    font-size: large;
}

.span-content-small {
    color: #a5a3a3;
    font-weight: 500;
    font-size: 15px;
}

.box-style {
    background-color: #eee;
    box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.2);
}

.storage-chip {
    position: absolute;
    top: -14px;
    /* Adjust the value to align the chip above the progress bar */
    left: 50%;
    /* Adjust if needed */
    transform: translateX(-50%);
}

.storage-progress-bar {
    margin-top: 20px;
    /* Make space for the chip */
}

.ravkol-chip {
    font-size: 0.7rem;
    height: 24px;
}


@media (max-width: 600px) {
    /* Example for small screens */


    .box {
        width: 100% !important;
        /* Full width on small screens */
        margin: 10px !important;
        /* Space between boxes */
        flex-direction: column !important;
        padding: 5px !important;
    }
}
</style>